<script lang="ts" setup>
import debounce from 'lodash-es/debounce';
import { useShortCodesStore } from '../../_runtime/stores/shortcodes';
import { ComponentsList } from '../../_runtime/enums/shortcodes';
import type { FormModel, ShortCodeOption } from '../../_runtime/services/ShortCodesService';
import ShortCodesService from '../../_runtime/services/ShortCodesService';

const route = useRoute();
const store = useShortCodesStore();
const $attrs = useAttrs();
const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();

const routeKey = ref('');
const search = ref('');
const pager = computed<Pager>(() => store.pagerListShortCodeOfGivenType as any);
const items = ref<ShortCodeOption[]>([]);
const currentProperties = (): Record<string, any> => only($attrs, 'class', 'style', 'properties');

const emit = defineEmits<{
    (e: 'edit', id: number): void;
}>();

const loadData = async (): Promise<void> =>
{
    pager.value.loading = true;

    try
    {
        const response = await ShortCodesService.shortCodes(currentProperties().properties.id, pager.value, search.value);

        pager.value.setTotalRows(response.totalRows);
        items.value = response.items;
    }
    catch (ex)
    {
        pager.value.setTotalRows(0);
        items.value = [];
        $log.debug(ex);
    }
    finally
    {
        persistState(routeKey.value, null, pager.value);
        pager.value.loading = false;
    }
};

onMounted(async (): Promise<void> =>
{
    routeKey.value = route.name.toString();
    restoreState(routeKey.value, null, pager.value);
    await loadData();
});

const handleSearch = debounce(async () =>
{
    if (search.value.length < 3) return;

    await loadData();
}, 500);

const clearSearch = async (): Promise<void> =>
{
    search.value = '';
    await loadData();
};

function goToShortCodesGrid()
{
    store.modalSection = ComponentsList.Grid;
}

function addNewShortCodeForGivenType()
{
    currentProperties().properties.editShortCodeId = 0;
    store.modalSection = ComponentsList.Form;
}

function editNewShortCodeForGivenType(id: number)
{
    emit('edit', id);
}

async function deleteItem(id: number)
{
    try
    {
        await ShortCodesService.deleteShortCode(id);

        $alert.success($t('[[[Shortcode został usunięty]]]'));
        await loadData();
    }
    catch (ex)
    {
        $log.debug(ex);
    }
}
</script>

<template>
    <DataCard ref="card" :use-panels="true" :fullscreen-panels="true" v-bind="currentProperties()">
        <!-- Toolbar -->
        <template #toolbar>
            <div class="toolbar-buttons">
                <ActionButton variant="secondary" icon="fa-solid fa-grid" :text="$t('[[[Wzory]]]')" @click.prevent="goToShortCodesGrid" />
                <ActionButton variant="primary" icon="fas fa-plus" :text="$t('[[[Dodaj ShortCode]]]')" @click.prevent="addNewShortCodeForGivenType" />
                <div class="input">
                    <input v-model="search" type="text" class="form-control" :placeholder="$t('[[[Wyszukaj]]]')" @input="handleSearch">
                    <i class="icon fa-solid fa-magnifying-glass" />
                    <i v-if="(search.length >= 3)" class="clear fa-solid fa-circle-xmark text-danger pointer" @click="clearSearch" />
                </div>
            </div>
        </template>
        <!-- Formularz -->
        <template #default>
            <ListView
                :items="items"
                :pager="pager"
                :row-click="(item: FormModel) => editNewShortCodeForGivenType(item.id)"
                @change="loadData()"
            >
                <template #row="{ item }">
                    <ListViewItem :title="$t('[[[Nazwa]]]')" sort="Name">
                        {{ item.name }}
                    </ListViewItem>
                    <ListViewItem :title="$t('[[[Data dodania]]]')" sort="DateCreatedUtc">
                        {{ $filters.datetime(item.dateCreatedUtc) }}
                    </ListViewItem>
                    <ListViewButtons>
                        <ListViewButton @click.prevent="editNewShortCodeForGivenType(item.id)">
                            <i class="fas fa-pencil-alt me-1" /> {{ $t('[[[Edytuj]]]') }}
                        </ListViewButton>
                        <ListViewButton :message="$t('[[[Potwierdzenie usunięcia]]]')" @confirm="deleteItem(item.id)">
                            <i class="fas fa-times me-2" /> {{ $t('[[[Usuń]]]') }}
                        </ListViewButton>
                    </ListViewButtons>
                </template>
            </ListView>
            <div class="d-flex flex-nowrap mt-auto justify-content-between">
                <Pagination :pager="pager" @change="loadData()" />
                <div class="d-flex align-items-center">
                    <PaginationInfo :pager="pager" class="ms-2 d-none d-sm-block d-md-block d-lg-block" />
                    <DataSize :pager="pager" class="ms-2" @change="loadData()" />
                </div>
            </div>
        </template>
    </DataCard>
</template>

<style scoped lang="scss">
.toolbar-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.toolbar-buttons > .btn {
    margin-right: 0;
}
.input {
    position: relative;
    margin-left: auto;

    input {
        padding: 8px 8px 8px 36px;
        background-color: var(--bs-white);
    }
    .icon {
        position: absolute;
        top: 12px;
        left: 12px;
        color: var(--bs-body-color);
    }
    .clear {
        position: absolute;
        top: 12px;
        right: 12px;
    }
}
</style>
